import React, { useState, useEffect, useRef, useCallback } from "react";
import { _t, getCookie } from "../../../../../functions/Functions";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { BASE_URL } from "../../../../../BaseUrl";
import { toast } from "react-toastify";
import { MdVpnKey } from "react-icons/md";

const UpdateDeviceTokenModal = ({
  deviceTokenModalItem,
  setDeviceTokenModalItem,
  setPaginatedSubmittedOrders,
}) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [deviceToken, setDeviceToken] = useState("");
  const inputRef = useRef(null);

  useEffect(() => {
    if (deviceTokenModalItem) {
      setDeviceToken(deviceTokenModalItem.device_token || "");
      const timer = setTimeout(() => {
        if (inputRef.current) {
          inputRef.current.focus();
          inputRef.current.select();
        }
      }, 120);
      return () => clearTimeout(timer);
    }
  }, [deviceTokenModalItem]);

  const handleClose = useCallback(() => {
    if (loading) return;
    setDeviceTokenModalItem(null);
  }, [loading, setDeviceTokenModalItem]);

  useEffect(() => {
    if (!deviceTokenModalItem) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape" && !loading) {
        handleClose();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [deviceTokenModalItem, loading, handleClose]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!deviceToken.trim()) {
      toast.error(_t(t("Device token cannot be empty")));
      return;
    }

    setLoading(true);
    const url = `${BASE_URL}/settings/update-device-token`;
    const payload = {
      id: deviceTokenModalItem?.id,
      device_token: deviceToken.trim(),
    };

    axios
      .post(url, payload, {
        headers: { Authorization: `Bearer ${getCookie()}` },
      })
      .then((res) => {
        setLoading(false);
        if (res.data) {
          toast.success(
            res.data?.message || _t(t("Device token updated successfully")),
            {
              position: "bottom-center",
              closeButton: false,
              autoClose: 3000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              className: "text-center toast-notification",
            }
          );
          if (deviceTokenModalItem) {
            deviceTokenModalItem.device_token = deviceToken.trim();
          }
          if (typeof setPaginatedSubmittedOrders === "function") {
            setPaginatedSubmittedOrders(1);
          }
        }
        setDeviceTokenModalItem(null);
      })
      .catch((err) => {
        setLoading(false);
        const errorMsg =
          err?.response?.data?.message || _t(t("Please try again"));
        toast.error(errorMsg, {
          position: "bottom-center",
          closeButton: false,
          autoClose: 6000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          className: "text-center toast-notification",
        });
      });
  };

  return (
    <div
      className={`modal fade ${deviceTokenModalItem ? "d-flex justify-content-center align-items-center showing" : ""}`}
      id="updateDeviceTokenModal"
      aria-hidden={!deviceTokenModalItem}
      tabIndex="-1"
      role="dialog"
      onClick={(e) => {
        if (e.target === e.currentTarget) {
          handleClose();
        }
      }}
      style={{
        zIndex: 1060,
        backgroundColor: "rgba(0, 0, 0, 0.45)",
      }}
    >
      <div
        className="modal-dialog modal-md m-0"
        role="document"
        style={{
          transform: "none",
          maxWidth: "460px",
          width: "92%",
        }}
      >
        <div
          className="modal-content shadow-lg border-0"
          style={{
            borderRadius: "14px",
            overflow: "hidden",
            background: "#ffffff",
          }}
        >
          {/* Header */}
          <div
            className="modal-header border-bottom py-2 px-3 d-flex align-items-center justify-content-between"
            style={{ background: "#f8fafc" }}
          >
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center justify-content-center mr-3"
                style={{
                  width: "40px",
                  height: "40px",
                  borderRadius: "10px",
                  background: "linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)",
                  color: "#0284c7",
                  boxShadow: "0 2px 6px rgba(2, 132, 199, 0.15)",
                }}
              >
                <MdVpnKey size={20} />
              </div>
              <div>
                <h5 className="modal-title font-weight-bold mb-0 text-dark" style={{ fontSize: "1.05rem" }}>
                  {_t(t("Update Device Token"))}
                </h5>
                <span className="text-muted" style={{ fontSize: "0.8rem" }}>
                  {_t(t("Self Order Configuration"))}
                </span>
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={handleClose}
              disabled={loading}
              aria-label="Close"
              title={_t(t("Close"))}
            ></button>
          </div>

          {/* Body */}
          <div className="modal-body p-4">
            {/* Centered Order Info Badge Box */}
            <div
              className="p-2 mb-3 rounded-lg border d-flex align-items-center justify-content-center flex-wrap"
              style={{
                background: "#f8fafc",
                borderColor: "#e2e8f0",
                gap: "15px",
              }}
            >

              {deviceTokenModalItem?.token?.id && (
                <div className="d-flex align-items-center">
                  <span className="text-muted mr-1" style={{ fontSize: "0.82rem" }}>
                    {_t(t("Token"))}:
                  </span>
                  <span className="badge badge-secondary px-2 py-1" style={{ fontSize: "0.82rem" }}>
                    #{deviceTokenModalItem.token.id}
                  </span>
                </div>
              )}

              {deviceTokenModalItem?.order_type_name && (
                <div className="d-flex align-items-center">
                  <span className="badge badge-info text-white px-2 py-1" style={{ fontSize: "0.82rem" }}>
                    {deviceTokenModalItem.order_type_name}
                  </span>
                </div>
              )}
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} autoComplete="off">
              <div className="form-group mb-3">
                <label
                  htmlFor="device_token_input"
                  className="font-weight-bold text-dark mb-1 d-flex align-items-center justify-content-between"
                  style={{ fontSize: "0.88rem" }}
                >
                  <span>
                    {_t(t("Device Token"))} <span className="text-danger">*</span>
                  </span>
                  {deviceToken && (
                    <button
                      type="button"
                      className="btn btn-link p-0 text-muted"
                      style={{ fontSize: "0.78rem", textDecoration: "none" }}
                      onClick={() => setDeviceToken("")}
                      disabled={loading}
                    >
                      {_t(t("Clear"))}
                    </button>
                  )}
                </label>
                <div className="position-relative">
                  <input
                    ref={inputRef}
                    id="device_token_input"
                    type="text"
                    name="device_token"
                    className="form-control"
                    placeholder={_t(t("Enter device token"))}
                    value={deviceToken}
                    onChange={(e) => setDeviceToken(e.target.value)}
                    disabled={loading}
                    required
                    style={{
                      height: "42px",
                      borderRadius: "8px",
                      border: "1px solid #cbd5e1",
                      paddingLeft: "14px",
                      fontSize: "0.95rem",
                      boxShadow: "none",
                    }}
                  />
                </div>
                <small className="form-text text-muted mt-2 text-center d-block" style={{ fontSize: "0.78rem" }}>
                  {_t(t("This token updates the device connection for this order."))}
                </small>
              </div>

              {/* Centered Actions Footer */}
              <div className="d-flex justify-content-center align-items-center pt-3 border-top mt-3">
                <button
                  type="button"
                  className="btn btn-secondary px-4 py-2 mr-2 rounded-sm"
                  onClick={handleClose}
                  disabled={loading}
                  style={{ fontSize: "0.88rem", minWidth: "110px" }}
                >
                  {_t(t("Cancel"))}
                </button>
                <button
                  type="submit"
                  disabled={loading || !deviceToken.trim()}
                  className="btn btn-primary px-4 py-2 rounded-sm d-flex align-items-center justify-content-center"
                  style={{
                    fontSize: "0.88rem",
                    minWidth: "130px",
                    fontWeight: 500,
                  }}
                >
                  {loading ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm mr-2"
                        role="status"
                        aria-hidden="true"
                      ></span>
                      {_t(t("Updating..."))}
                    </>
                  ) : (
                    _t(t("Update Token"))
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UpdateDeviceTokenModal;
